import { useState } from "react";
import {
	type Control,
	Controller,
	type UseFormSetValue,
} from "react-hook-form";
import { Button } from "@/components/ui/button";
import { mockUserImages } from "@/components/user/mock/mockUserImages";
import sprites from "/src/shared/assets/sprite.svg";
import type { FormSchema } from "../../../types";
import { DeleteImageDialog } from "../DeleteImageDialog";
import { UploadImageDialog } from "../UploadImageDialog";

interface ProfileImageManagerProps {
	control: Control<FormSchema>;
	setValue: UseFormSetValue<FormSchema>;
}

export const ProfileImageManager = ({
	control,
	setValue,
}: ProfileImageManagerProps) => {
	const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	return (
		<Controller
			control={control}
			name="imageId"
			render={({ field: { value, onChange } }) => {
				const selectedImage =
					mockUserImages.find((img) => img.imageId === value)?.imageSrc || null;

				const handleUpload = (imageSrc: string) => {
					const foundImage = mockUserImages.find(
						(img) => img.imageSrc === imageSrc,
					);

					if (foundImage) {
						onChange(foundImage.imageId);
						setValue("imageId", foundImage.imageId, {
							shouldDirty: true,
						});
					} else {
						onChange(null);
						setValue("imageId", 0, { shouldDirty: true });
					}
				};

				const handleDelete = () => {
					onChange(null);
					setIsDeleteDialogOpen(false);
					setValue("imageId", 0, { shouldDirty: true, shouldValidate: true });
				};

				return (
					<div
						className="flex flex-col gap-1"
						data-testid="profile-image-manager"
					>
						<div className="flex flex-col gap-5">
							<div className="w-20 h-20">
								{selectedImage ? (
									<img
										src={selectedImage}
										alt="User Avatar"
										className="w-full h-full rounded-full"
									/>
								) : (
									<svg
										aria-label="profile"
										role="img"
										fill="#fff"
										className="w-full h-full"
									>
										<use xlinkHref={`${sprites}#profile`} />
									</svg>
								)}
							</div>

							<p className="text-lg">Avatar</p>
						</div>

						<div className="flex gap-2">
							<Button
								variant="dropdown"
								className="text-[16px] w-fit"
								onClick={() => setIsUploadDialogOpen(true)}
							>
								Upload Photo
							</Button>
							{selectedImage && (
								<Button
									variant="dropdown"
									className="text-[16px] w-fit"
									onClick={() => setIsDeleteDialogOpen(true)}
								>
									Delete Photo
								</Button>
							)}
						</div>

						<UploadImageDialog
							open={isUploadDialogOpen}
							onOpenChange={setIsUploadDialogOpen}
							onUpload={handleUpload}
							selectedImg={selectedImage}
						/>
						<DeleteImageDialog
							open={isDeleteDialogOpen}
							onOpenChange={setIsDeleteDialogOpen}
							onDelete={handleDelete}
						/>
					</div>
				);
			}}
		/>
	);
};
